/**
 * Hover labels (spec 1) and hover previews (spec 3), without the DOM: show on mouse hover after a delay or on keyboard
 * focus, never on touch; hide on leave, blur, Escape or dismiss (scroll). Only one per layer shows at a time.
 */
export const TOOLTIP_SHOW_DELAY_MS = 300;

export interface IHoverHostEvent {
  key?: string;
  pointerType?: string;
  type: string;
}

export interface IHoverHost {
  addEventListener(type: string, listener: (event: IHoverHostEvent) => void): void;
  matches(selector: string): boolean;
  removeEventListener(type: string, listener: (event: IHoverHostEvent) => void): void;
  setAttribute(name: string, value: string): void;
}

export interface ITimers {
  clear: (handle: number) => void;
  set: (fn: () => void, ms: number) => number;
}

export interface IHoverView<T> {
  hide: () => void;
  show: (host: IHoverHost, content: T) => void;
}

export interface IHoverBinding<T> {
  setContent: (content: T) => void;
  unbind: () => void;
}

export interface IHoverLayer<T> {
  bind: (host: IHoverHost, content: T) => IHoverBinding<T>;
  dismiss: () => void;
}

interface IHoverLayerDeps<T> {
  delayMs: number;
  isFocusShown: boolean;
  timers: ITimers;
  view: IHoverView<T>;
}

export function createHoverLayer<T>({ delayMs, isFocusShown, timers, view }: IHoverLayerDeps<T>): IHoverLayer<T> {
  let shownHost: IHoverHost | null = null;
  let pendingTimer: number | null = null;

  const cancelPending = () => {
    if (pendingTimer !== null) timers.clear(pendingTimer);
    pendingTimer = null;
  };

  const dismiss = () => {
    cancelPending();
    if (shownHost === null) return;
    shownHost = null;
    view.hide();
  };

  const bind = (host: IHoverHost, initial: T): IHoverBinding<T> => {
    let content = initial;
    const show = () => {
      cancelPending();
      shownHost = host;
      view.show(host, content);
    };
    const hide = () => {
      if (shownHost === host || pendingTimer !== null) dismiss();
    };
    const listeners: Record<string, (event: IHoverHostEvent) => void> = {
      blur: hide,
      focus: () => {
        if (isFocusShown && host.matches(':focus-visible')) show();
      },
      keydown: (event) => {
        if (event.key === 'Escape') hide();
      },
      pointerenter: (event) => {
        if (event.pointerType === 'touch') return;
        cancelPending();
        pendingTimer = timers.set(show, delayMs);
      },
      pointerleave: hide,
    };
    Object.entries(listeners).forEach(([type, fn]) => host.addEventListener(type, fn));
    return {
      setContent: (next) => {
        content = next;
        if (shownHost === host) view.show(host, content);
      },
      unbind: () => {
        Object.entries(listeners).forEach(([type, fn]) => host.removeEventListener(type, fn));
        hide();
      },
    };
  };

  return { bind, dismiss };
}

export function bindTooltip(layer: IHoverLayer<string>, host: IHoverHost, text: string): IHoverBinding<string> {
  host.setAttribute('aria-label', text);
  const binding = layer.bind(host, text);
  return {
    setContent: (next) => {
      host.setAttribute('aria-label', next);
      binding.setContent(next);
    },
    unbind: binding.unbind,
  };
}
